import { encode } from 'blurhash'
import { cmsApi } from './api'

// The cover's blurhash, as the media job computes it (4×3 from a copy at most
// 32 px a side), so the app knows its colors from the first view.
function blurhashOf(image: CanvasImageSource, width: number, height: number) {
  const ratio = Math.min(32 / width, 32 / height, 1)
  const sampleWidth = Math.max(1, Math.round(width * ratio))
  const sampleHeight = Math.max(1, Math.round(height * ratio))
  const canvas = document.createElement('canvas')
  canvas.width = sampleWidth
  canvas.height = sampleHeight
  const context = canvas.getContext('2d')
  if (!context) return null
  context.drawImage(image, 0, 0, sampleWidth, sampleHeight)
  const { data } = context.getImageData(0, 0, sampleWidth, sampleHeight)
  return encode(data, sampleWidth, sampleHeight, 4, 3)
}

export async function prepareImage(file: File) {
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
    throw new Error('Selecione um arquivo JPG, PNG ou WEBP.')
  }

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(new Error('Não foi possível ler a imagem.'))
    reader.readAsDataURL(file)
  })
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const element = new Image()
    element.onload = () => resolve(element)
    element.onerror = () => reject(new Error('Não foi possível preparar a imagem.'))
    element.src = dataUrl
  })

  const maxSide = 2200
  const ratio = Math.min(maxSide / image.width, maxSide / image.height, 1)
  const width = Math.max(1, Math.round(image.width * ratio))
  const height = Math.max(1, Math.round(image.height * ratio))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Seu navegador não conseguiu preparar a imagem.')
  context.drawImage(image, 0, 0, width, height)

  return {
    dataUrl: canvas.toDataURL('image/jpeg', 0.9),
    label: `${file.name} · ${width}×${height} · JPG`,
    // Of the JPG that is uploaded, which is what the app shows.
    blurhash: blurhashOf(canvas, width, height),
    width,
    height,
  }
}

// Where the Guve keeps its images; anything else still lives on another site.
export const isGuveImage = (url: string | null | undefined) => Boolean(url?.startsWith('https://media.guve.com.br/'))

// An image prepared and kept in the browser until the editor saves (PRD.md,
// item 2): its preview address is local (blob:), and nothing reaches the
// Guve's bucket for an image replaced or an editor discarded.
export type PreparedImage = Awaited<ReturnType<typeof prepareImage>>
export const isLocalPreview = (url: string | null | undefined) => Boolean(url?.startsWith('blob:'))

// Decoded here, not fetched: the CMS's CSP (public/_headers) doesn't let it
// fetch a data: address.
export async function previewOf(prepared: PreparedImage) {
  const bytes = Uint8Array.from(atob(prepared.dataUrl.split(',')[1]), (character) => character.charCodeAt(0))
  return URL.createObjectURL(new Blob([bytes], { type: 'image/jpeg' }))
}

// On save: stored as the app shows it, at most 2,200 px, JPG, in the Guve's
// bucket; an event cover also records its blurhash, so the app knows its
// colors from the first view.
export async function storePrepared(prepared: PreparedImage, kind: 'event' | 'place' | 'producer') {
  const result = await cmsApi.uploadImage(prepared.dataUrl, kind)
  if (kind === 'event' && prepared.blurhash) {
    void cmsApi.saveMediaBlurhash({
      url: result.image_url, blurhash: prepared.blurhash, width: prepared.width, height: prepared.height,
    }).catch(() => {})
  }
  return result.image_url
}

// The images prepared for a place's or profile's form, by their local preview:
// a draft or a save sends each one to the Guve once (PRD.md, item 98).
const prepared = new Map<string, PreparedImage>()
const stored = new Map<string, Promise<string>>()

export const rememberPrepared = (preview: string, image: PreparedImage) => { prepared.set(preview, image) }

// The Guve's address for an image still in the browser; others as they are.
export function storeLocal(url: string | null, kind: 'place' | 'producer'): Promise<string | null> {
  if (!url || !isLocalPreview(url)) return Promise.resolve(url)
  const image = prepared.get(url)
  if (!image) return Promise.resolve(null)
  if (!stored.has(url)) {
    stored.set(url, storePrepared(image, kind).catch((error) => { stored.delete(url); throw error }))
  }
  return stored.get(url)!
}
